const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉'

const toSubscript = (n: number) =>
  String(n)
    .split('')
    .map((digit) => SUBSCRIPT_DIGITS[Number(digit)])
    .join('')

/**
 * Rewrites every number in already-formatted en-US text whose fraction opens with `minZeros`+
 * zeros into the subscript form: "0.00042 BTC" → "0.0₃42 BTC", "-0.0000123" → "-0.0₄123".
 * Works on the digits the caller already chose to show (no re-rounding), so a value formatted
 * down to "0.00" stays "0.00", and "10.0005" / "1,000.0001" (integer part ≠ 0) are untouched.
 */
export function compactZeroRuns(enUs: string, minZeros = 3): string {
  return enUs.replace(/(^|[^\d.,])0\.(0+)(?=[1-9])/g, (match, prefix: string, zeros: string) =>
    zeros.length >= minZeros ? `${prefix}0.0${toSubscript(zeros.length)}` : match
  )
}

/**
 * A tiny number with its run of leading fractional zeros counted in a subscript, as crypto UIs
 * show sub-satoshi-scale amounts: 0.0000042 → "0.0₅42" (5 zeros, then the significant digits).
 * Keeps a coin-denominated PnL readable in a narrow table cell instead of rounding it to 0.00.
 *
 * Returns en-US text (pass it through `fmt.text`), or null when the value doesn't need it —
 * zero, ≥ 1, or fewer than `minZeros` leading zeros — so the caller keeps its normal format.
 */
export function compactLeadingZeros(
  value: number,
  { significantDigits = 2, minZeros = 3 }: { significantDigits?: number; minZeros?: number } = {}
): string | null {
  const abs = Math.abs(value)
  if (!Number.isFinite(abs) || abs === 0 || abs >= 1) return null

  let zeros = -Math.floor(Math.log10(abs)) - 1
  let digits = Math.round(abs * 10 ** (zeros + significantDigits))
  // Rounding can carry into another digit (0.0000999 → 100): one fewer leading zero.
  if (digits >= 10 ** significantDigits) {
    zeros -= 1
    digits = Math.round(digits / 10)
  }
  if (zeros < minZeros) return null

  const significant = String(digits).replace(/0+$/, '') || '0'
  return `${value < 0 ? '-' : ''}0.0${toSubscript(zeros)}${significant}`
}
